import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import {
  BonusProgram, EligibilityFlagDetail, EligibilityFlagInfo, EligibilityRuleInfo, EmployeeOption, FLAG_LABEL, FlagStatus, employeeLabel, errText, money,
} from './bonusTypes';

interface Props {
  programs: BonusProgram[];
  canEnter: boolean;
  canDecide: boolean;
  isSuperUser: boolean;
  /** Called when a decision changes what the other tabs show (held earnings). */
  onChanged?: () => void;
}

const statusColor = (s: FlagStatus): 'warning' | 'success' | 'error' => (s === 'UNDER_REVIEW' ? 'warning' : s === 'ELIGIBLE' ? 'success' : 'error');
const when = (v: string | null) => (v ? new Date(v.includes('T') ? v : `${v.replace(' ', 'T')}Z`).toLocaleString() : '');

const EligibilityPanel: React.FC<Props> = ({ programs, canEnter, canDecide, isSuperUser, onChanged }) => {
  const [flags, setFlags] = useState<EligibilityFlagInfo[]>([]);
  const [rules, setRules] = useState<EligibilityRuleInfo[]>([]);
  const [status, setStatus] = useState<string>('UNDER_REVIEW');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([bonusTrackerAPI.eligibilityFlags(status === 'ALL' ? undefined : status), bonusTrackerAPI.eligibilityRules(true)]);
      setFlags(f);
      setRules(r);
    } catch (e) {
      setMessage({ type: 'error', text: errText(e) });
    }
  }, [status]);
  useEffect(() => { void load(); }, [load]);

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Notes such as a write-up, a suspension or an absence are recorded here with their evidence. A note never disqualifies anyone by itself:
          the earnings it touches wait, and cannot be approved, until an authorized person decides if the employee is eligible. Nothing is removed or paid automatically.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {isSuperUser && <Button variant="outlined" onClick={() => setManaging(true)}>Reasons</Button>}
          {canEnter && <Button variant="contained" onClick={() => setCreating(true)}>Flag an employee</Button>}
        </Stack>
      </Stack>
      <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="UNDER_REVIEW">Under review</MenuItem>
          <MenuItem value="INELIGIBLE">Not eligible</MenuItem>
          <MenuItem value="ELIGIBLE">Eligible</MenuItem>
          <MenuItem value="ALL">All</MenuItem>
        </Select>
      </FormControl>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Held earnings</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {flags.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>{f.employeeName}{f.employeeNumber ? ` (${f.employeeNumber})` : ''}</TableCell>
                <TableCell>{f.ruleName}</TableCell>
                <TableCell>{f.startDate === f.endDate ? f.startDate : `${f.startDate} to ${f.endDate}`}</TableCell>
                <TableCell sx={{ maxWidth: 260 }}><Typography variant="body2" noWrap title={f.note}>{f.note}</Typography>
                  {f.evidenceCount > 0 && <Typography variant="caption" color="text.secondary">{f.evidenceCount} evidence file(s)</Typography>}
                </TableCell>
                <TableCell><Chip size="small" color={statusColor(f.status)} label={FLAG_LABEL[f.status]} /></TableCell>
                <TableCell align="right">{f.heldEarnings || '-'}</TableCell>
                <TableCell align="right"><Button size="small" onClick={() => setOpenId(f.id)}>Open</Button></TableCell>
              </TableRow>
            ))}
            {!flags.length && (
              <TableRow><TableCell colSpan={7}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No eligibility reviews with this status.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {creating && (
        <FlagDialog rules={rules.filter((r) => r.active)} onClose={() => setCreating(false)}
          onSaved={(text) => { setCreating(false); setMessage({ type: 'success', text }); void load(); onChanged?.(); }} />
      )}
      {openId && (
        <FlagDetail id={openId} canDecide={canDecide} canEnter={canEnter} onClose={() => setOpenId(null)}
          onChanged={(text) => { if (text) setMessage({ type: 'success', text }); void load(); onChanged?.(); }} />
      )}
      {managing && <RulesDialog rules={rules} programs={programs} onClose={() => setManaging(false)} onChanged={() => { void load(); }} />}
    </Box>
  );
};

/* ------------------------------ new review ------------------------------ */

const FlagDialog: React.FC<{ rules: EligibilityRuleInfo[]; onClose: () => void; onSaved: (text: string) => void }> = ({ rules, onClose, onSaved }) => {
  const [ruleId, setRuleId] = useState('');
  const [employee, setEmployee] = useState<EmployeeOption | null>(null);
  const [options, setOptions] = useState<EmployeeOption[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (text: string) => {
    try { setOptions(await bonusTrackerAPI.employees(text, 'ALL', undefined, true)); } catch { /* typing still works */ }
  }, []);
  useEffect(() => { void search(''); }, [search]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await bonusTrackerAPI.createEligibilityFlag({ ruleId, employeeId: employee!.id, startDate, endDate: endDate || startDate, note });
      if (files.length) await bonusTrackerAPI.uploadEligibilityEvidence(created.id, files);
      const held = created.heldEarnings ? ` ${created.heldEarnings} earning(s) the employee already has in these dates are now held until it is decided.` : '';
      onSaved(`Eligibility review opened.${held}`);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Flag an employee</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Autocomplete size="small" options={options} value={employee} getOptionLabel={employeeLabel} isOptionEqualToValue={(a, b) => a.id === b.id} filterOptions={(x) => x}
          onInputChange={(_e, v, reason) => { if (reason === 'input') void search(v); }} onChange={(_e, v) => setEmployee(v)}
          renderInput={(params) => <TextField {...params} label="Employee" required />} />
        <FormControl size="small" fullWidth required>
          <InputLabel>Reason</InputLabel>
          <Select label="Reason" value={ruleId} onChange={(e) => setRuleId(e.target.value)}>
            {rules.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}{r.programName ? ` · ${r.programName}` : ''}</MenuItem>)}
          </Select>
        </FormControl>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" type="date" required fullWidth label="First day it covers" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <TextField size="small" type="date" fullWidth label="Last day (optional)" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Stack>
        <TextField size="small" required multiline minRows={2} label="What the note says (keep the original wording)" value={note} onChange={(e) => setNote(e.target.value)} />
        <Box>
          <Button component="label" size="small" variant="outlined">
            Add evidence (optional)
            <input hidden type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </Button>
          {files.length > 0 && <Typography variant="caption" sx={{ ml: 1 }}>{files.map((f) => f.name).join(', ')}</Typography>}
        </Box>
        {!rules.length && <Alert severity="info">There are no eligibility reasons yet. A super user sets them up under Reasons.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !employee || !ruleId || !startDate || !note.trim()}>Open review</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------ review detail ------------------------------ */

const FlagDetail: React.FC<{ id: string; canDecide: boolean; canEnter: boolean; onClose: () => void; onChanged: (text?: string) => void }> = ({ id, canDecide, canEnter, onClose, onChanged }) => {
  const [flag, setFlag] = useState<EligibilityFlagDetail | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setFlag(await bonusTrackerAPI.eligibilityFlag(id)); } catch (e) { setError(errText(e)); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const decide = async (decision: 'ELIGIBLE' | 'INELIGIBLE') => {
    setBusy(true);
    setError(null);
    try {
      setFlag(await bonusTrackerAPI.decideEligibilityFlag(id, { decision, note }));
      setNote('');
      onChanged(decision === 'ELIGIBLE' ? 'Decided: eligible. The earnings held by this review can now be approved.' : 'Decided: not eligible. Earnings already on record stay until someone removes them.');
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try { setFlag(await bonusTrackerAPI.uploadEligibilityEvidence(id, files)); onChanged(); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const openFile = async (attachmentId: string) => {
    try { window.open(URL.createObjectURL(await bonusTrackerAPI.eligibilityEvidenceBlob(id, attachmentId)), '_blank', 'noopener'); } catch (e) { setError(errText(e)); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{flag ? `${flag.ruleName} · ${flag.employeeName}` : 'Eligibility review'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {flag && (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip color={statusColor(flag.status)} label={FLAG_LABEL[flag.status]} />
              <Typography variant="body2">{flag.startDate === flag.endDate ? flag.startDate : `${flag.startDate} to ${flag.endDate}`} · opened by {flag.createdByName || 'unknown'}</Typography>
            </Stack>
            <Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{flag.note}</Typography></Paper>
            {flag.decisionNote && (
              <Alert severity={flag.status === 'ELIGIBLE' ? 'success' : 'error'}>
                {FLAG_LABEL[flag.status]} · {flag.decidedByName} · {when(flag.decidedAt)}: {flag.decisionNote}
              </Alert>
            )}
            <Box>
              <Typography variant="subtitle2">Evidence</Typography>
              {flag.attachments.map((a) => (
                <Button key={a.id} size="small" onClick={() => void openFile(a.id)}>{a.fileName}</Button>
              ))}
              {!flag.attachments.length && <Typography variant="body2" color="text.secondary">No files yet.</Typography>}
              {canEnter && (
                <Button component="label" size="small" disabled={busy}>
                  Add files
                  <input hidden type="file" multiple onChange={(e) => { void addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                </Button>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2">Earnings held by this review</Typography>
              {flag.heldLines.map((l) => (
                <Typography key={l.id} variant="body2">{l.programName} · bonus ending {l.periodEnd} · {l.earningDate}{l.reference ? ` · ${l.reference}` : ''} · {money(l.amount)}</Typography>
              ))}
              {!flag.heldLines.length && <Typography variant="body2" color="text.secondary">None. New earnings in these dates will be held, or refused if the decision is not eligible.</Typography>}
            </Box>
            {canDecide && (
              <Box>
                <TextField size="small" fullWidth multiline minRows={2} required label="Note for the decision" value={note} onChange={(e) => setNote(e.target.value)} />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button variant="contained" color="success" disabled={busy || !note.trim() || flag.status === 'ELIGIBLE'} onClick={() => void decide('ELIGIBLE')}>Eligible</Button>
                  <Button variant="contained" color="error" disabled={busy || !note.trim() || flag.status === 'INELIGIBLE'} onClick={() => void decide('INELIGIBLE')}>Not eligible</Button>
                </Stack>
              </Box>
            )}
            {!canDecide && flag.status === 'UNDER_REVIEW' && <Alert severity="info">A super user or the administration approver decides this review.</Alert>}
            <Box>
              <Typography variant="subtitle2">History</Typography>
              {flag.events.map((e, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">{when(e.createdAt)} · {e.actorName} · {e.action.replace(/_/g, ' ').toLowerCase()}{e.note ? ` · ${e.note}` : ''}</Typography>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

/* ------------------------------ reasons (super user) ------------------------------ */

const RulesDialog: React.FC<{ rules: EligibilityRuleInfo[]; programs: BonusProgram[]; onClose: () => void; onChanged: () => void }> = ({ rules, programs, onClose, onChanged }) => {
  const [f, setF] = useState({ name: '', programId: 'ALL', effectiveFrom: '', effectiveTo: '', description: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await bonusTrackerAPI.createEligibilityRule({ ...f, programId: f.programId === 'ALL' ? null : f.programId, effectiveFrom: f.effectiveFrom || null, effectiveTo: f.effectiveTo || null });
      setF({ name: '', programId: 'ALL', effectiveFrom: '', effectiveTo: '', description: '' });
      onChanged();
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  const toggle = async (r: EligibilityRuleInfo) => {
    try { await bonusTrackerAPI.updateEligibilityRule(r.id, { active: !r.active }); onChanged(); } catch (e) { setError(errText(e)); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Eligibility reasons</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2" color="text.secondary">
          A reason names what can affect eligibility (for example Suspension). It can apply to every program or to one, and only inside its own dates. Turning a reason off stops it applying; earlier reviews stay on record.
        </Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Reason</TableCell><TableCell>Program</TableCell><TableCell>Dates</TableCell><TableCell>In use</TableCell></TableRow></TableHead>
          <TableBody>
            {rules.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.programName || 'All programs'}</TableCell>
                <TableCell>{r.effectiveFrom || r.effectiveTo ? `${r.effectiveFrom || 'start'} to ${r.effectiveTo || 'no end'}` : 'Always'}</TableCell>
                <TableCell><Switch size="small" checked={r.active} onChange={() => void toggle(r)} inputProps={{ 'aria-label': `${r.name} in use` }} /></TableCell>
              </TableRow>
            ))}
            {!rules.length && <TableRow><TableCell colSpan={4}><Typography color="text.secondary" sx={{ textAlign: 'center' }}>No reasons yet.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
        <Typography variant="subtitle2">New reason</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" required fullWidth label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <FormControl size="small" fullWidth>
            <InputLabel>Applies to</InputLabel>
            <Select label="Applies to" value={f.programId} onChange={(e) => setF({ ...f, programId: e.target.value })}>
              <MenuItem value="ALL">All programs</MenuItem>
              {programs.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" type="date" fullWidth label="Starts (optional)" InputLabelProps={{ shrink: true }} value={f.effectiveFrom} onChange={(e) => setF({ ...f, effectiveFrom: e.target.value })} />
          <TextField size="small" type="date" fullWidth label="Ends (optional)" InputLabelProps={{ shrink: true }} value={f.effectiveTo} onChange={(e) => setF({ ...f, effectiveTo: e.target.value })} />
        </Stack>
        <TextField size="small" label="Description (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={add} disabled={busy || !f.name.trim()}>Add reason</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EligibilityPanel;
